import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Loader2, History, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCardSplits, useDeleteCardSplit } from './useCardSplits';
import { PAYMENT_KIND_LABELS } from './types';
import type { CardSplit } from './types';

function formatBrl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

interface MachineGroup {
  machineId: string | null;
  machineName: string;
  count: number;
  totalGross: number;
  totalNet: number;
  totalCost: number;
}

export function SplitsHistory() {
  const { data: splits = [], isLoading } = useCardSplits();
  const remove = useDeleteCardSplit();
  const [confirmTarget, setConfirmTarget] = useState<CardSplit | null>(null);

  const summary = useMemo<MachineGroup[]>(() => {
    const map = new Map<string, MachineGroup>();
    for (const s of splits) {
      const key = s.card_machine_id ?? '__none__';
      const existing = map.get(key) ?? {
        machineId: s.card_machine_id ?? null,
        machineName: s.machine?.name ?? '—',
        count: 0,
        totalGross: 0,
        totalNet: 0,
        totalCost: 0,
      };
      existing.count += 1;
      existing.totalGross += Number(s.gross_amount);
      existing.totalNet += Number(s.net_amount);
      existing.totalCost += Number(s.gross_amount) - Number(s.net_amount);
      map.set(key, existing);
    }
    return Array.from(map.values()).sort((a, b) => b.totalGross - a.totalGross);
  }, [splits]);

  return (
    <div className="space-y-4">
      {/* Resumo por maquininha */}
      {summary.length > 0 && (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
          {summary.map((g) => (
            <Card key={g.machineId ?? '__none__'}>
              <CardContent className="p-3">
                <p className="font-semibold text-foreground">{g.machineName}</p>
                <p className="text-xs text-muted-foreground">
                  {g.count} antecipaç{g.count === 1 ? 'ão' : 'ões'}
                </p>
                <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                  <span className="text-muted-foreground">Bruto</span>
                  <span className="text-right tabular-nums">{formatBrl(g.totalGross)}</span>
                  <span className="text-muted-foreground">Líquido</span>
                  <span className="text-right tabular-nums text-income">
                    {formatBrl(g.totalNet)}
                  </span>
                  <span className="text-muted-foreground">Custo total</span>
                  <span className="text-right tabular-nums text-expense">
                    − {formatBrl(g.totalCost)}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : splits.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <History className="mx-auto mb-3 h-10 w-10 opacity-40" />
              <p className="text-sm">Nenhuma antecipação registrada ainda.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/40 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 text-left">Data</th>
                    <th className="px-3 py-2 text-left">Maquininha</th>
                    <th className="px-3 py-2 text-left">Forma</th>
                    <th className="px-3 py-2 text-right">Bruto</th>
                    <th className="px-3 py-2 text-right">MDR</th>
                    <th className="px-3 py-2 text-right">Antec.</th>
                    <th className="px-3 py-2 text-right">Líquido</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {splits.map((s) => (
                    <tr key={s.id} className="border-b border-border/50">
                      <td className="px-3 py-2 text-xs">
                        {format(new Date(`${s.transaction_date}T00:00:00`), 'dd/MM/yyyy', {
                          locale: ptBR,
                        })}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div className="font-medium">{s.machine?.name ?? '—'}</div>
                        {s.description && (
                          <div className="text-muted-foreground">{s.description}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {PAYMENT_KIND_LABELS[s.payment_kind]}
                        {s.payment_kind === 'credit_installments' && (
                          <span className="text-muted-foreground"> · {s.installments}×</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums">
                        {formatBrl(Number(s.gross_amount))}
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums text-expense">
                        − {formatBrl(Number(s.mdr_amount))}
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums text-expense">
                        {Number(s.anticipation_amount) > 0
                          ? `− ${formatBrl(Number(s.anticipation_amount))}`
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-xs font-semibold tabular-nums text-income">
                        {formatBrl(Number(s.net_amount))}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setConfirmTarget(s)}
                          disabled={remove.isPending}
                          className="h-6 w-6 text-muted-foreground hover:text-expense"
                        >
                          <Trash2 size={12} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmTarget} onOpenChange={(v) => !v && setConfirmTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir antecipação?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmTarget?.transaction_id
                ? 'A transação financeira vinculada também será removida.'
                : 'O registro será removido do histórico.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmTarget) remove.mutate(confirmTarget);
                setConfirmTarget(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
